import React, { useCallback, useEffect, useState } from 'react';
import { Button, Checkbox, Form, Input, Radio, Select } from 'antd';
import { FilterGroup, Grid } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { FilterSelector } from '../../../elements/filter-selector';

export function GridDetailsForm(props: { grid?: Grid; onSaved?: (grid: Grid) => void }) {
  const [loading, setLoading] = useState(false);
  const [parentOptions, setParentOptions] = useState<any[]>();
  const [readOnlyFilterGroup, setReadOnlyFilterGroup] = useState<FilterGroup>();
  const [passiveFilterGroup, setPassiveFilterGroup] = useState<FilterGroup>();
  const [customIconFilterGroup, setCustomIconFilterGroup] = useState<FilterGroup>();
  const [form] = Form.useForm();
  const [grid, setGrid] = useState<Grid>();

  const isSubGrid = !!props.grid?.parentFieldId;

  useEffect(() => {
    setGrid(props.grid);
  }, [props.grid]);

  useEffect(() => {
    loadParentOptions();
  }, []);

  useEffect(() => {
    setReadOnlyFilterGroup(props.grid?.readOnlyFilterGroup);
    setPassiveFilterGroup(props.grid?.passiveFilterGroup);
    setCustomIconFilterGroup(props.grid?.customIconFilterGroup);
  }, [form, props.grid]);

  const loadParentOptions = () => {
    apiClient
      .get('grid-groups')
      .then((response: any) => {
        const options = response.data.map((g: Grid) => ({ value: g.id, label: g.name }));
        setParentOptions(options);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      });
  };

  const loadGrid = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`grids/${props.grid?.id}`)
      .then((response: any) => {
        setGrid(response.data);
        props.onSaved?.(response.data);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [props]);

  const onEditFinish = (values: any) => {
    if (readOnlyFilterGroup) {
      values.readOnlyFilterGroup = readOnlyFilterGroup;
    }
    if (passiveFilterGroup) {
      values.passiveFilterGroup = passiveFilterGroup;
    }
    if (customIconFilterGroup) {
      values.customIconFilterGroup = customIconFilterGroup;
    }

    if (isSubGrid) {
      values.name = grid?.name;
      values.description = grid?.description;
      values.priority = 1;
    }

    setLoading(true);

    apiClient
      .put(`grids/${props.grid?.id}`, values)
      .then(() => {
        loadGrid();
      })
      .catch((reason: any) => {
        setLoading(false);
        apiErrorMessage(reason);
      });
  };

  if (!grid) {
    return null;
  }

  return (
    <Form layout="vertical" onFinish={onEditFinish} initialValues={grid} form={form}>
      {!isSubGrid ? (
        <Form.Item
          label="İsim"
          name="name"
          wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}
          rules={[
            { required: true, message: 'Zorunlu alan' },
            {
              pattern: /^[a-zA-Z0-9ıİğĞüÜşŞöÖçÇ][a-zA-Z0-9_\-ıİğĞüÜşŞöÖçÇ\s]*$/,
              message: 'Sadece harf, rakam, boşluk, - ve _ karakteri kullanılabilir',
            },
            { max: 100, message: 'En fazla 100 karakter' },
          ]}
        >
          <Input />
        </Form.Item>
      ) : (
        <Form.Item label="İsim" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
          <span className="ant-form-text">{grid?.name}</span>
        </Form.Item>
      )}
      <Form.Item label="Kod" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
        <span className="ant-form-text">{grid?.code}</span>
      </Form.Item>
      {!isSubGrid && (
        <>
          <Form.Item
            label="Açıklama"
            name="description"
            wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}
            rules={[
              { required: true, message: 'Zorunlu alan' },
              { max: 400, message: 'En fazla 400 karakter' },
            ]}
          >
            <Input.TextArea />
          </Form.Item>

          <Form.Item label="Veri seti grubu" name="groupId" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
            <Select allowClear>
              {parentOptions?.map(o => (
                <Select.Option value={o.value} key={o.value}>
                  {o.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Önem seviyesi"
            name="priority"
            wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}
            rules={[{ required: true, message: 'Zorunlu alan' }]}
          >
            <Radio.Group>
              <Radio.Button value={1}>!</Radio.Button>
              <Radio.Button value={2}>!!</Radio.Button>
              <Radio.Button value={3}>!!!</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label="Mükerrerlik algoritması"
            name="duplicateAlgorithm"
            wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}
          >
            <Select allowClear>
              <Select.Option value="PLM_Item">PLM Kalem</Select.Option>
            </Select>
          </Form.Item>
        </>
      )}

      <Form.Item
        name="allowRecordWithErrors"
        valuePropName="checked"
        wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}
      >
        <Checkbox>Kural hatalı kayıt oluşturulabilsin</Checkbox>
      </Form.Item>

      {grid?.id && (
        <>
          <Form.Item label="Salt okunabilirlik kuralı" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
            <FilterSelector dataSetId={grid.id} value={readOnlyFilterGroup} onChange={setReadOnlyFilterGroup} size="small" />
          </Form.Item>

          <Form.Item label="Pasif kayıt kuralı" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
            <FilterSelector dataSetId={grid.id} value={passiveFilterGroup} onChange={setPassiveFilterGroup} size="small" />
          </Form.Item>

          <Form.Item label="Özel kayıt simgesi kuralı" wrapperCol={{ span: 8, xs: 24, sm: 24, md: 12, lg: 10, xl: 10, xxl: 8 }}>
            <FilterSelector dataSetId={grid.id} value={customIconFilterGroup} onChange={setCustomIconFilterGroup} size="small" />
          </Form.Item>
        </>
      )}

      <Form.Item>
        <Button type="primary" htmlType="submit" loading={loading}>
          Kaydet
        </Button>
      </Form.Item>
    </Form>
  );
}
